import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { enablePush, disablePush, getPushSubscriptionStatus, isPushSupported, isIOSNonStandalone } from "@/lib/webPush";

/**
 * PushToggleButton — bottone unico che attiva/disattiva le notifiche push.
 * Mostra hint iOS specifici. Non renderizza nulla se il browser non supporta.
 */
export default function PushToggleButton({ compact = false }) {
  const [state, setState] = useState({ supported: false, subscribed: false, permission: "default" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPushSubscriptionStatus().then(setState).catch(() => setState({ supported: false, subscribed: false }));
  }, []);

  if (!state.supported) {
    if (typeof window !== "undefined" && !isPushSupported()) return null;
  }

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (state.subscribed) {
        await disablePush();
        toast.success("Notifiche push disattivate");
      } else {
        if (isIOSNonStandalone()) {
          toast.info("Su iPhone/iPad: installa l'app dalla schermata Home (Condividi → Aggiungi a Home), poi riapri.");
          return;
        }
        await enablePush();
        toast.success("Notifiche attive — riceverai messaggi anche quando l'app è chiusa");
      }
      const s = await getPushSubscriptionStatus();
      setState(s);
    } catch (e) {
      toast.error(e.message || "Impossibile attivare le notifiche");
    } finally { setBusy(false); }
  };

  const label = state.subscribed ? "Notifiche attive" : "Attiva notifiche";
  const Icon = state.subscribed ? Bell : BellOff;

  if (compact) {
    return (
      <button data-testid="push-toggle-compact" onClick={toggle} disabled={busy}
        title={label}
        className={`p-2 rounded-full transition-colors ${state.subscribed ? "bg-primary/10 text-primary" : "hover:bg-muted text-muted-foreground"}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" strokeWidth={1.5} />}
      </button>
    );
  }

  return (
    <button data-testid="push-toggle-btn" onClick={toggle} disabled={busy}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
        state.subscribed ? "bg-primary/10 text-primary border border-primary/40" : "border border-input hover:border-primary"
      }`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Icon className="w-4 h-4" strokeWidth={1.5} />}
      {label}
    </button>
  );
}
